// 接続矢印の経路・形状の更新（object.connector.update）を、接続矢印ごとに 1 件ずつ順番に送るための
// 送信キュー。
//
// op は種別ごと・送信ごとに別の実行で処理されるため、送信順に確定する保証が無い。経路・形状の更新は
// 「変更する項目の変更前の値」（expected）をサーバが現在値と照合するため、同じ接続矢印へ続けて送った
// 2 件が入れ替わって届くと、後の操作（前の操作の結果を変更前の値として持つ）が先に照合されて競合として
// 拒否される（単独の利用者が区間を続けてドラッグしただけで起きる）。これをクライアント側の送信の
// 直列化で解く（表の table-op-queue と同じ形）。
//
// 楽観適用は待たない（画面は即座に更新する）。ここが担うのは「送信の順序と 1 件ずつの待機」だけで、
// 楽観適用・巻き戻しは従来どおり doc store が行う。

import type { UpdateConnectorOp } from "@refinement-canvas/domain";
import {
  CONNECTOR_EDITING_UNAVAILABLE_MESSAGE,
  connectorEditingAvailableNow,
} from "./connector-editing-capability";
import { useCanvasDocStore } from "./store/canvas-doc-store";

/**
 * 接続矢印 1 本あたりの未送信の待機上限。上限到達後の新しい操作は受け付けない（呼び出し側の
 * dispatcher が楽観適用を取り消し、利用者へ通知する）。
 */
export const CONNECTOR_QUEUE_MAX_PENDING = 64;

/** 待機上限に達して操作を受け付けられないときに、利用者へ伝える文言。 */
export const CONNECTOR_QUEUE_FULL_MESSAGE =
  "接続矢印の未送信の変更が多すぎます。通信が回復して保存が進むまで、少し待ってからお試しください。";

/**
 * 応答（op.commit / op.reject）を待つ期限。超過したら後続送信を止めて再同期へ移る。
 * 接続が切れている間は数えない（送信は再接続の後に行われるため、再接続からこの期限を待つ）。
 */
export const CONNECTOR_QUEUE_RESPONSE_TIMEOUT_MS = 20_000;

/** 応答期限の監視間隔。期限そのものより十分に細かく刻む。 */
const CONNECTOR_QUEUE_DEADLINE_TICK_MS = 1_000;

/** キューが呼び出し元へ依頼する操作。テスト時は差し替えられるよう関数の集合で受け取る。 */
export interface ConnectorOpQueueHost {
  /** 実際に op を送信する（dispatcher の realtime.sendOp）。 */
  readonly send: (op: UpdateConnectorOp) => void;
  /**
   * 破棄した op の楽観適用を取り消す。`sent` は送信済み（応答待ち）の op か。送っていない op の破棄
   * （先行の op の拒否・作成の拒否・サーバの対応の喪失）では false で、呼び出し側は実行されなかった
   * 操作として扱える（取り消し・やり直しの再生なら、履歴を元へ戻す）。
   */
  readonly dropOptimistic: (clientOpId: string, sent: boolean) => void;
  /** 利用者への通知（上限・期限切れ）。 */
  readonly notify: (message: string) => void;
  /** 権威スナップショットの取り直しを要求する。 */
  readonly requestResync: () => void;
  /**
   * リアルタイム通信が接続中か。切断中は送信経路が op を溜めて再接続の後に送るため、応答期限を数えない
   * （切断中に期限切れで楽観表示と履歴を取り消した後、再接続で op が届いて変更が戻るのを防ぐ）。
   * 省略時は常に接続中として扱う。
   */
  readonly isConnected?: (() => boolean) | undefined;
}

interface QueueEntry {
  /** 送信済みで応答待ちの op。1 本につき常に 1 件以下。 */
  inFlight: UpdateConnectorOp | null;
  /** 応答待ちの絶対期限（epoch ms）。 */
  deadlineAt: number | null;
  /** 未送信の op（FIFO）。 */
  pending: UpdateConnectorOp[];
}

const queues = new Map<string, QueueEntry>();
let host: ConnectorOpQueueHost | null = null;
let unsubscribeDocStore: (() => void) | null = null;
let deadlineTimer: ReturnType<typeof setInterval> | null = null;

/** 接続矢印ごとの待機状態（テスト用の読み取り専用のスナップショット）。 */
export function connectorOpQueueSnapshot(objectId: string): {
  readonly inFlightClientOpId: string | null;
  readonly pendingCount: number;
} {
  const entry = queues.get(objectId);
  return {
    inFlightClientOpId: entry?.inFlight?.clientOpId ?? null,
    pendingCount: entry?.pending.length ?? 0,
  };
}

/**
 * キューの依頼先を設定する。`null` を渡すと購読とタイマーを解除し、待機列も破棄する
 * （canvas 切替・破棄時に別 canvas へ送らないため）。
 */
export function configureConnectorOpQueue(next: ConnectorOpQueueHost | null): void {
  host = next;
  if (next === null) {
    resetConnectorOpQueues();
    return;
  }
  ensureDocStoreSubscription();
  // 依頼先が無い間に積まれた待機列を、ここで送り始める（送られないまま滞留させない）。
  for (const objectId of [...queues.keys()]) pump(objectId);
}

/** すべての待機状態を破棄する（canvas 切替・テストの後始末）。 */
export function resetConnectorOpQueues(): void {
  queues.clear();
  stopDeadlineTimer();
  unsubscribeDocStore?.();
  unsubscribeDocStore = null;
}

/**
 * op を待機列へ積む。上限に達している場合は積まずに false を返す（呼び出し側は楽観適用を取り消す）。
 * 待機列の op は統合しない（経路と形状の更新はそれぞれ利用者の 1 操作で、履歴と 1 対 1 に対応するため）。
 */
export function enqueueConnectorOp(op: UpdateConnectorOp): boolean {
  const entry = queues.get(op.objectId) ?? { inFlight: null, deadlineAt: null, pending: [] };
  if (entry.pending.length >= CONNECTOR_QUEUE_MAX_PENDING) {
    host?.notify(CONNECTOR_QUEUE_FULL_MESSAGE);
    return false;
  }
  queues.set(op.objectId, entry);
  entry.pending.push(op);
  pump(op.objectId);
  return true;
}

/**
 * 指定した接続矢印へ、それぞれ指定した件数だけ待機上限を超えずに積めるか（積みはしない）。
 * 取り消し・やり直しの再生の前に確かめ、1 つの履歴エントリの op の一部だけが送られるのを防ぐ。
 */
export function canEnqueueConnectorOps(objectIds: readonly string[]): boolean {
  const counts = new Map<string, number>();
  for (const objectId of objectIds) counts.set(objectId, (counts.get(objectId) ?? 0) + 1);
  for (const [objectId, count] of counts) {
    const pending = queues.get(objectId)?.pending.length ?? 0;
    if (pending + count > CONNECTOR_QUEUE_MAX_PENDING) return false;
  }
  return true;
}

/**
 * リアルタイム通信が再接続したときに呼ぶ。応答待ちの期限を再接続の時点から数え直す（切断中にタブが
 * 休止して期限の監視タイマーが動かなかった場合も、切断の前に決めた期限で取り消さない）。
 */
export function restartConnectorOpDeadlines(): void {
  const now = Date.now();
  for (const entry of queues.values()) {
    if (entry.deadlineAt !== null) entry.deadlineAt = now + CONNECTOR_QUEUE_RESPONSE_TIMEOUT_MS;
  }
}

/**
 * 接続矢印の op がサーバに拒否されたことを伝える（`op.reject` の受信経路から呼ぶ）。
 *
 * 拒否は「その接続矢印に対する自分の想定（変更前の値）が最新でない」ことを意味する。待機列の op は
 * 同じ想定の続きで組み立てられているため、続けて送っても同じ理由で拒否される。そこで後続送信を止め、
 * 未送信 op を楽観適用ごと破棄する。拒否された op 自身の楽観適用は受信経路の `onServerReject` が
 * 巻き戻し、再同期も既存の `op.reject` 経路が行う。
 */
export function notifyConnectorOpRejected(clientOpId: string): void {
  for (const [objectId, entry] of queues) {
    if (entry.inFlight?.clientOpId !== clientOpId) continue;
    entry.inFlight = null;
    entry.deadlineAt = null;
    // 取り消しの通知は doc store を更新して購読を呼び戻すため、先に待機列を片付けてから通知する。
    const discarded = entry.pending;
    entry.pending = [];
    queues.delete(objectId);
    for (const op of discarded) host?.dropOptimistic(op.clientOpId, false);
    stopDeadlineTimerIfIdle();
    return;
  }
}

/** 送信できる状態なら、待機列の先頭を 1 件だけ送る。 */
function pump(objectId: string): void {
  const entry = queues.get(objectId);
  if (!entry || !host) return;
  if (entry.inFlight !== null) return;
  const next = entry.pending[0];
  if (next === undefined) {
    queues.delete(objectId);
    stopDeadlineTimerIfIdle();
    return;
  }
  // 作成直後は、その接続矢印の object.create の確定も先行依存として待つ
  // （create より先に着いた更新は、対象不在の no-op として確定し、楽観表示だけが残るため）。
  if (isCreatePending(objectId)) return;
  // 作成が拒否された・他の利用者に削除された（確定状態に無い）接続矢印への更新は送らない。送っても
  // サーバは対象不在の no-op として確定するだけで、実行されなかった変更の履歴が残る。待機列を楽観適用ごと
  // 破棄する（履歴の組は楽観適用の取り消しの通知で除かれる）。
  if (useCanvasDocStore.getState().committedObjects[objectId]?.type !== "connector") {
    discardPending(objectId, entry, host);
    return;
  }
  // 待機の間にサーバの対応を確認できなくなった（取り直し・再接続の取得の失敗、旧サーバへの切り戻し）
  // 場合も送らない。旧サーバは未知の op に応答しないため、送ると応答期限まで楽観表示だけが残る。
  if (!connectorEditingAvailableNow()) {
    discardPending(objectId, entry, host);
    host.notify(CONNECTOR_EDITING_UNAVAILABLE_MESSAGE);
    return;
  }
  entry.pending.shift();
  entry.inFlight = next;
  entry.deadlineAt = Date.now() + CONNECTOR_QUEUE_RESPONSE_TIMEOUT_MS;
  ensureDeadlineTimer();
  host.send(next);
}

/**
 * 応答待ちの無い接続矢印の待機列を、送らずに楽観適用ごと破棄する。取り消しの通知は doc store を更新して
 * 購読を呼び戻すため、先に待機列を片付けてから行う。
 */
function discardPending(objectId: string, entry: QueueEntry, target: ConnectorOpQueueHost): void {
  const discarded = entry.pending;
  entry.pending = [];
  queues.delete(objectId);
  for (const op of discarded) target.dropOptimistic(op.clientOpId, false);
  stopDeadlineTimerIfIdle();
}

/** 対象の接続矢印の object.create が、まだ確定していない（in-flight に残っている）か。 */
function isCreatePending(objectId: string): boolean {
  return useCanvasDocStore
    .getState()
    .inFlightOps.some((op) => op.type === "object.create" && op.object.id === objectId);
}

/**
 * doc store の in-flight から消えた（＝確定または拒否された）in-flight op を検出し、次を送る。
 * commit / reject のどちらでも doc store が in-flight から取り除くため、1 か所で両方を扱える。
 */
function ensureDocStoreSubscription(): void {
  if (unsubscribeDocStore) return;
  unsubscribeDocStore = useCanvasDocStore.subscribe((state) => {
    const pumpTargets: string[] = [];
    for (const [objectId, entry] of queues) {
      const inFlight = entry.inFlight;
      if (inFlight === null) {
        // object.create 待ちで送り出せずにいる待機列も、確定の契機で送り始められるか試す。
        if (entry.pending.length > 0) pumpTargets.push(objectId);
        continue;
      }
      if (state.inFlightOps.some((op) => op.clientOpId === inFlight.clientOpId)) continue;
      entry.inFlight = null;
      entry.deadlineAt = null;
      pumpTargets.push(objectId);
    }
    for (const objectId of pumpTargets) pump(objectId);
    stopDeadlineTimerIfIdle();
  });
}

function ensureDeadlineTimer(): void {
  if (deadlineTimer !== null || typeof window === "undefined") return;
  deadlineTimer = setInterval(checkDeadlines, CONNECTOR_QUEUE_DEADLINE_TICK_MS);
}

function stopDeadlineTimer(): void {
  if (deadlineTimer === null) return;
  clearInterval(deadlineTimer);
  deadlineTimer = null;
}

function stopDeadlineTimerIfIdle(): void {
  for (const entry of queues.values()) {
    if (entry.inFlight !== null) return;
  }
  stopDeadlineTimer();
}

/**
 * 応答期限を過ぎた接続矢印の後続送信を止め、権威スナップショットの取り直しへ移る。
 *
 * 応答が届かなかった op は、保存できたかどうかを判断できない。楽観適用をそのまま残すと
 * 「保存された」という誤った表示が resync 後も居座るため、未送信分と同じく楽観適用を取り消し、
 * 権威スナップショットの内容へ収束させる（実際には保存できていた場合、その内容は
 * スナップショットまたは後続の op.commit で戻る）。新しい id での自動再送はしない。
 */
function checkDeadlines(): void {
  const now = Date.now();
  // 切断中は応答が届きようがないため期限を数えず、再接続から改めて期限を待つ。
  if (host?.isConnected && !host.isConnected()) {
    for (const entry of queues.values()) {
      if (entry.deadlineAt !== null) entry.deadlineAt = now + CONNECTOR_QUEUE_RESPONSE_TIMEOUT_MS;
    }
    return;
  }
  let expired = false;
  const discarded: { readonly op: UpdateConnectorOp; readonly sent: boolean }[] = [];
  for (const [objectId, entry] of queues) {
    if (entry.inFlight === null || entry.deadlineAt === null || entry.deadlineAt > now) continue;
    expired = true;
    discarded.push({ op: entry.inFlight, sent: true });
    entry.inFlight = null;
    entry.deadlineAt = null;
    discarded.push(...entry.pending.map((op) => ({ op, sent: false })));
    entry.pending = [];
    queues.delete(objectId);
  }
  // 取り消しの通知は購読を呼び戻すため、待機列をすべて片付けてから行う。
  for (const { op, sent } of discarded) host?.dropOptimistic(op.clientOpId, sent);
  if (!expired) {
    stopDeadlineTimerIfIdle();
    return;
  }
  host?.notify("接続矢印の変更を保存できたか確認できませんでした。最新の内容を取得し直します。");
  host?.requestResync();
  stopDeadlineTimerIfIdle();
}
